<template>
  <ContentSection>
    <div
      :class="[
        'relative my-8 grid w-full',
        {
          'bg-gradient-02 text-white': data.background_color?.value === 'gradient-01',
          'bg-grey-100': data.background_color?.value === 'grey',
        },
      ]"
    >
      <!-- eslint-disable-next-line vue/first-attribute-linebreak -->
      <Container class="
        flex w-full flex-col
        md:grid md:grid-cols-[1fr_2fr] md:gap-x-12
        xl:grid-cols-[300px_3fr_1fr]
      "
      >
        <!-- Cover Image -->
        <div class="hidden size-full items-center justify-center md:flex">
          <AppImage
            v-if="data.cover_image"
            :src="data.cover_image"
            class="flex-1 translate-x-[15%] scale-[1.15] shadow-[3px_6px_8px_0px_rgba(0,0,0,0.3)]"
            sizes="100vw"
          />
        </div>

        <!-- Text Content with Background -->
        <div class="relative flex items-center py-24 md:pl-24">
          <!-- Content Container -->
          <div class="w-full">
            <div class="flex flex-col">
              <h3 class="font-semibold typo-h4">
                {{ data.title }}
              </h3>
              <p class="typo-lead">
                {{ subtitleResolved }}
              </p>
            </div>

            <!-- Description -->
            <div
              v-if="data.description"
              class="mt-2 wysiwyg"
              :class="{ 'wysiwyg-negative': data.background_color?.value === 'gradient-01' }"
              v-html="data.description"
            />

            <!-- Mobile Cover Image -->
            <div class="mt-8 md:hidden">
              <AppImage
                v-if="data.cover_image"
                :src="data.cover_image"
                class="max-h-64 w-full object-contain"
              />
            </div>

            <!-- External Link -->
            <NuxtLink
              v-if="downloadUrl"
              class="
                mt-8 flex items-center font-bold transition-opacity ease-in-out typo-body
                hover:opacity-50
              "
              :to="downloadUrl"
              target="_blank"
            >
              <span class="pr-3">{{ downloadButtonText }}</span>
              <IconArrowUpRight class="size-6" />
            </NuxtLink>
          </div>
        </div>
      </Container>
    </div>
  </ContentSection>
</template>

<script setup lang="ts">
import type { MainComponentsDocumentDownloadFragment } from '#gql'

const { t } = useI18n()

const props = defineProps<{ data: Enforce<MainComponentsDocumentDownloadFragment> }>()

const subtitleResolved = computed(() => props.data.subtitle || filenameFromPermalink(props.data.file?.permalink))

const downloadButtonText = computed(() => {
  // Use custom text if provided
  if (props.data.download_button_text) {
    return props.data.download_button_text
  }

  return t('download_here')
})

const filenameFromPermalink = (permalink?: string | null): string => {
  if (!permalink) return ''
  try {
    const url = new URL(permalink)
    const parts = url.pathname.split('/')
    return decodeURIComponent(parts[parts.length - 1])
  } catch {
    const parts = permalink.split('/')
    return decodeURIComponent(parts[parts.length - 1])
  }
}

const downloadUrl = computed(() => {
  if (props.data.file) {
    return props.data.file.permalink
  }

  return props.data.external_link
})
</script>
